import React from 'react';
import PropTypes from 'prop-types';
import { ToastContext, Modal, Button, Message, CheckboxLabel, TooltipHelp, Numbers } from '@weezevent/nacre';
import * as Sentry from '@sentry/browser';
import i18n from 'i18next';

import { useTranslation, Trans } from 'react-i18next';
import { useForm } from '../../../utils/validators';
import useAppContext from '../../../context/useAppContext';
import { useNotifications, triggerToast } from '../../../components/notifications/notificationsProvider';
import Style from './refundPostAction.module.css';

export default function RefundPostAction({ actionInstance = null, onClose = () => {} }) {
    const { t } = useTranslation();
    const [{ event }] = useAppContext();
    const { addNotification } = useNotifications();
    const locale = i18n.language;

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [form, setForm] = useForm({
        delete_all: false
    });

    const data = React.useMemo(() => {
        let _data = {
            delete_all: form.delete_all,
            items: actionInstance.mode === 'attendees' ? actionInstance.filtered_attendees : actionInstance.filtered_orders,
            mode: actionInstance.mode
        };
        return _data;
    }, [form, actionInstance]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .runPostAction(data)
            .then(response => {
                triggerToast(
                    data.mode,
                    actionInstance,
                    data.items.length,
                    response,
                    t,
                    toast,
                    addNotification,
                    `ticket.manager.${data.mode}.action.${actionInstance.constructor.NAME}.toast.`
                );
                onClose();
                setIsSubmitting(false);
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t(`ticket.manager.${data.mode}.action.` + actionInstance.constructor.NAME + '.toast.error', { count: data.items.length }));
                setIsSubmitting(false);
            });
    }, [actionInstance, onClose, data, addNotification]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    return (
        <Modal
            size="large"
            open={Boolean(actionInstance)}
            onClose={_onClose}
            title={actionInstance ? t('ticket.manager.' + data.mode + '.action.' + actionInstance.constructor.NAME + '.title', { count: data.items.length }) : ''}
            subtitle={
                actionInstance ? (
                    <>
                        <Trans i18nKey={`ticket.manager.${data.mode}.action.${actionInstance.constructor.NAME}.subtitle`} />
                        <Numbers value={actionInstance.total_amount} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                    </>
                ) : (
                    ''
                )
            }
            submitButton={<Button primary onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />}
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            <div>
                <div className={Style['info-text']}>{t('ticket.manager.attendees.action.refund.info-text')}</div>
                <div className={Style['checkbox-and-tooltip']}>
                    <CheckboxLabel
                        label={t('ticket.manager.attendees.action.refund.checkbox-action')}
                        onChange={(e, { checked }) => setForm('delete_all', checked)}
                        checked={form.delete_all}
                    />
                    <TooltipHelp triggerOnHover position="bottom" title={t('ticket.manager.attendees.action.refund.tooltip')} />
                </div>
            </div>
            <Message className={Style['warning_message']} type="default" title={t('ticket.manager.attendees.action.refund.message-title')}>
                <div className={Style['warning_message_content']}>
                    <div>{t('ticket.manager.attendees.action.refund.message-block-1')}</div>
                    <div>• {t('ticket.manager.attendees.action.refund.message-block-2')}</div>
                    <div>• {t('ticket.manager.attendees.action.refund.message-block-3')}</div>
                    <br />
                    <div>{t('ticket.manager.attendees.action.refund.message-block-4')}</div>
                </div>
            </Message>
        </Modal>
    );
}

RefundPostAction.propTypes = {
    actionInstance: PropTypes.object,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    bodyContent: PropTypes.node
};
